import { api } from './client';

export type NotificationCategory = 'Safety' | 'Health' | 'DeploymentLifecycle' | 'TradeExecution' | 'BacktestJobs';

export interface NotificationCategorySetting {
  category: NotificationCategory;
  eligible: boolean;
  allowed: boolean;
  defaultEnabled: boolean;
  override: boolean | null;
  effectiveEnabled: boolean;
}

export interface TelegramDestination {
  linked: boolean;
  chatType: string | null;
  title: string | null;
  username: string | null;
  healthy: boolean;
  linkedUtc: string | null;
  lastSuccessUtc: string | null;
  lastFailureUtc: string | null;
  lastError: string | null;
}

export interface NotificationResource {
  id: string;
  name: string;
  type: 'Gateway' | 'Deployment';
  gatewayId: string | null;
  status: string | null;
}

export interface MyNotifications {
  botConfigured: boolean;
  globallyEnabled: boolean;
  botUsername: string | null;
  destination: TelegramDestination;
  categories: NotificationCategorySetting[];
  tradingScopeMode: 'All' | 'Selected';
  gatewayIds: string[];
  deploymentIds: string[];
  resources: NotificationResource[];
}

export interface TelegramPolicy {
  safetyAllowed: boolean; healthAllowed: boolean; lifecycleAllowed: boolean; tradeAllowed: boolean; backtestAllowed: boolean;
  safetyDefault: boolean; healthDefault: boolean; lifecycleDefault: boolean; tradeDefault: boolean; backtestDefault: boolean;
  opsHealthEnabled: boolean; opsTradeExceptionsEnabled: boolean; opsBacktestFailuresEnabled: boolean;
}

export interface TelegramAdminConfiguration {
  enabled: boolean; botConfigured: boolean; botId: string | null; botUsername: string | null; botDisplayName: string | null;
  publicBaseUrl: string | null; tokenUpdatedUtc: string | null; lastPollUtc: string | null; lastPollSuccessUtc: string | null;
  lastPollError: string | null; readiness: string; operationsDestination: TelegramDestination; policy: TelegramPolicy;
  deliveryCounts: { queued: number; retrying: number; sent: number; deadLettered: number };
  audit: Array<{ id: number; userDisplay: string; action: string; outcome: string; detail: string | null; occurredUtc: string }>;
}

export interface NotificationDelivery {
  id: string; eventType: string; category: string; severity: string; destination: string; status: string;
  attempts: number; createdUtc: string; nextAttemptUtc: string; sentUtc: string | null; lastError: string | null;
}
export interface NotificationDeliveryPage { items: NotificationDelivery[]; nextBeforeUtc: string | null; hasMore: boolean }

export const notificationsApi = {
  mine: (signal?: AbortSignal) => api.get<MyNotifications>('/api/notifications/me', { signal }),
  createLink: () => api.post<{ url: string; expiresUtc: string }>('/api/notifications/me/link'),
  unlink: () => api.delete<void>('/api/notifications/me/link'),
  savePreferences: (request: { categories: Record<string, boolean | null>; tradingScopeMode: string; gatewayIds: string[]; deploymentIds: string[] }) =>
    api.put<void>('/api/notifications/me/preferences', request),
  test: () => api.post<void>('/api/notifications/me/test'),
  admin: (signal?: AbortSignal) => api.get<TelegramAdminConfiguration>('/api/admin/telegram', { signal }),
  saveBot: (request: { token: string; publicBaseUrl: string; takeOverWebhook: boolean; confirmBotChange: boolean }) =>
    api.put<void>('/api/admin/telegram/bot', request),
  removeBot: () => api.delete<void>('/api/admin/telegram/bot?confirmed=true'),
  saveConfiguration: (request: { enabled: boolean; publicBaseUrl: string; policy: TelegramPolicy }) =>
    api.put<void>('/api/admin/telegram/configuration', request),
  createOperationsLink: () => api.post<{ url: string; expiresUtc: string }>('/api/admin/telegram/operations-link'),
  unlinkOperations: () => api.delete<void>('/api/admin/telegram/operations-link'),
  testOperations: () => api.post<void>('/api/admin/telegram/operations-test'),
  deliveries: (signal?: AbortSignal) => api.get<NotificationDeliveryPage>('/api/admin/telegram/deliveries?limit=50', { signal }),
  retry: (id: string) => api.post<void>(`/api/admin/telegram/deliveries/${encodeURIComponent(id)}/retry`),
};
